import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import AiCreditIcon from 'src/assets/icons/AiCreditIcon.svg';
import BuyCreditsDialog from 'src/components/buyCreditsDialog';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { getStoreItems } from 'src/redux/actions/store';
import CareerProPitch, { type CareerProPitchProps } from './CareerProPitch';
import { CheckoutHead } from './styles';

type ProPitchSheetProps = Omit<
  CareerProPitchProps,
  'premium' | 'loading' | 'busy' | 'onGoPro' | 'note'
> & {
  /** What opened it, for the payment funnel; passed to the checkout. */
  reason?: string;
  /** The AI-credit wall the candidate hit, said above the list. */
  shortfall?: {
    balance: number;
    /** What the blocked action costs and what it is, where the opener knows. */
    needed?: number;
    action?: string;
  };
};

/**
 * The pitch with its own checkout, for a surface that is already a sheet -- the
 * tailor dialog, the AI-credit top-up. Go Pro turns this same sheet into the
 * checkout instead of stacking the store's dialog on top of it, and Pro bought
 * here comes back to the page it was bought from.
 */
const ProPitchSheet = ({ shortfall, reason, ...props }: ProPitchSheetProps) => {
  const dispatch = useAppDispatch();
  const shopItems = useAppSelector((state) => state.store.shopItems);
  const [checkoutId, setCheckoutId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!shopItems.length) dispatch(getStoreItems());
  }, [dispatch, shopItems.length]);

  const premium = shopItems.find(
    (i) => i.item_type === 'premium_profile_standard',
  );

  // Why the pitch opened, said first: what the task needs and what they have.
  const note = shortfall && (
    <>
      <img src={AiCreditIcon} alt="" width={16} height={16} />
      <span>
        {shortfall.needed && shortfall.action ? (
          <>
            {shortfall.action} needs <b>{shortfall.needed} AI credits</b>. You
            have <b>{shortfall.balance}</b>.
          </>
        ) : (
          <>
            You have <b>{shortfall.balance} AI credits</b>, not enough for{' '}
            {shortfall.action
              ? shortfall.action[0].toLowerCase() + shortfall.action.slice(1)
              : 'this'}
            .
          </>
        )}
      </span>
    </>
  );

  // The pitch showing Go Pro was pressed on, carried into the checkout.
  const [instanceId, setInstanceId] = React.useState<string>();
  const goPro = (fromInstance: string, itemId?: string) => {
    setInstanceId(fromInstance);
    const id = itemId ?? premium?.id;
    if (id) setCheckoutId(id);
  };

  if (checkoutId) {
    return (
      <Stack sx={{ minHeight: 0 }}>
        <CheckoutHead>
          <IconButton
            onClick={() => setCheckoutId(null)}
            size="small"
            aria-label="Back to Abekus Pro"
          >
            <ArrowBackRounded fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Abekus Pro
          </Typography>
        </CheckoutHead>
        <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 1, md: 1.5 } }}>
          <BuyCreditsDialog
            embedded
            open
            trigger="out_of_credits"
            reason={reason}
            funnelInstanceId={instanceId}
            directPurchase={{ itemId: checkoutId }}
            // Pro lands on its success page, which offers the way back here.
            // Held by the checkout itself, so a checkout abandoned at any step
            // leaves nothing behind for a later, unrelated purchase. The lead
            // need tells that page what the pass was bought for.
            context={{ returnHere: true, lead: props.lead }}
            // Razorpay takes over from here; this sheet goes back to the pitch
            // underneath it, so dismissing the payment isn't a dead end.
            onClose={() => setCheckoutId(null)}
          />
        </Box>
      </Stack>
    );
  }

  return (
    <CareerProPitch
      {...props}
      note={note}
      premium={premium}
      loading={!shopItems.length}
      onGoPro={goPro}
    />
  );
};

export default ProPitchSheet;
